import { apiClient } from '@/api/client';
import type { GetTutorsResponse, GetTutorsParams, GetRecommendedTutorsParams, GetRecommendedTutorsResponse, TutorData } from '@/types/api';

/**
 * GET /api/tutors/recommended
 * Get recommended tutors for carousel display
 * Returns the newest approved active tutors with pagination support
 * Public endpoint - no authentication required (optional auth for preferences)
 */
export const getRecommendedTutors = async (
  params?: GetRecommendedTutorsParams
): Promise<GetRecommendedTutorsResponse['data']> => {
  const response = await apiClient.get<GetRecommendedTutorsResponse>('/tutors/recommended', { params });

  if (!response.data.success) {
    throw new Error('Failed to fetch recommended tutors');
  }

  return response.data.data;
};

/**
 * GET /api/tutors
 * Get all approved tutors with flexible filtering and pagination
 * Public endpoint - no authentication required
 */
export const getTutors = async (
  params?: GetTutorsParams,
  signal?: AbortSignal
): Promise<GetTutorsResponse['data']> => {
  const response = await apiClient.get<GetTutorsResponse>('/tutors', { params, signal });

  if (!response.data.success) {
    throw new Error('Failed to fetch tutors');
  }

  return response.data.data;
};

/**
 * GET /api/tutors/:id
 * Get a specific tutor by ID
 * Public endpoint - no authentication required (optional auth for preferences)
 */
export const getTutorById = async (
  id: string,
  source?: string,
  signal?: AbortSignal
): Promise<TutorData> => {
  const response = await apiClient.get<{ success: true; data: TutorData }>(`/tutors/${id}`, {
    params: source ? { source } : undefined,
    signal,
  });

  if (!response.data.success) {
    throw new Error('Failed to fetch tutor');
  }

  return response.data.data;
};
